// Offline Queue Service
// Manages offline requests and integrates with service worker background sync

export interface OfflineRequest {
  id?: number;
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string | null;
  timestamp: number;
  retryCount: number;
  maxRetries?: number;
  priority?: 'low' | 'normal' | 'high';
  tags?: string[];
}

export interface OfflineQueueStats {
  totalRequests: number;
  pendingRequests: number;
  failedRequests: number;
  successfulRequests: number;
  oldestRequest?: number;
  newestRequest?: number;
}

export interface OfflineQueueOptions {
  maxRetries?: number;
  retryDelay?: number;
  maxQueueSize?: number;
  enableBackgroundSync?: boolean;
  syncInterval?: number;
  syncSchedule?: {
    immediate?: boolean;        // Sync ngay khi có request mới
    onVisibilityChange?: boolean; // Sync khi user quay lại tab
    intervals?: number[];       // Multiple sync intervals [30s, 2min, 5min]
    maxAge?: number;           // Sync requests older than X milliseconds
    batchSize?: number;        // Số lượng requests sync cùng lúc
    retryIntervals?: number[]; // Retry intervals [5s, 30s, 2min]
  };
}

export class OfflineQueueService {
  private dbName = 'offline-queue';
  private dbVersion = 1;
  private storeName = 'requests';
  private options: Required<OfflineQueueOptions>;
  private syncInterval?: number;
  private syncIntervals: number[] = [];
  private retryTimeouts: Map<number, number> = new Map();
  private isOnline: boolean = navigator.onLine;
  private lastSyncTime: number = 0;

  constructor(options: OfflineQueueOptions = {}) {
    this.options = {
      maxRetries: options.maxRetries || 3,
      retryDelay: options.retryDelay || 5000,
      maxQueueSize: options.maxQueueSize || 1000,
      enableBackgroundSync: options.enableBackgroundSync !== false,
      syncInterval: options.syncInterval || 30000,
      syncSchedule: {
        immediate: options.syncSchedule?.immediate !== false,
        onVisibilityChange: options.syncSchedule?.onVisibilityChange !== false,
        intervals: options.syncSchedule?.intervals || [30000, 120000, 300000], // 30s, 2min, 5min
        maxAge: options.syncSchedule?.maxAge || 3600000, // 1 hour
        batchSize: options.syncSchedule?.batchSize || 10,
        retryIntervals: options.syncSchedule?.retryIntervals || [5000, 30000, 120000], // 5s, 30s, 2min
        ...options.syncSchedule
      }
    };

    this.init();
  }

  /**
   * Initialize the service
   */
  private async init(): Promise<void> {
    try {
      // Initialize IndexedDB
      await this.openDB();
      
      // Set up online/offline detection
      this.setupOnlineDetection();
      
      // Set up visibility change detection
      this.setupVisibilityChangeDetection();
      
      // Set up background sync if enabled
      if (this.options.enableBackgroundSync) {
        this.setupBackgroundSync();
      }
      
      // Start advanced sync scheduling if online
      if (this.isOnline) {
        this.startAdvancedSync();
      }
      
      // Clean up old requests on init
      await this.cleanupOldRequests();
      
      console.log('Offline Queue Service initialized successfully with advanced sync');
    } catch (error) {
      console.error('Failed to initialize Offline Queue Service:', error);
    }
  }

  /**
   * Open IndexedDB connection
   */
  private async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          
          // Create indexes for better performance
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('method', 'method', { unique: false });
          store.createIndex('url', 'url', { unique: false });
          store.createIndex('priority', 'priority', { unique: false });
          store.createIndex('retryCount', 'retryCount', { unique: false });
        }
      };
    });
  }

  /**
   * Set up online/offline detection
   */
  private setupOnlineDetection(): void {
    const updateOnlineStatus = () => {
      this.isOnline = navigator.onLine;
      
      if (this.isOnline) {
        console.log('Network connection restored, starting advanced sync...');
        this.startAdvancedSync();
        this.processQueueWithBatch();
      } else {
        console.log('Network connection lost, requests will be queued');
        this.stopAdvancedSync();
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
  }

  /**
   * Set up background sync
   */
  private setupBackgroundSync(): void {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then((registration) => {
        // Register background sync
        registration.sync.register('offline-queue').catch((error) => {
          console.warn('Background sync registration failed:', error);
        });
      });
    }
  }

  /**
   * Start advanced sync scheduling
   */
  private startAdvancedSync(): void {
    this.stopAdvancedSync();
    
    // Set up multiple sync intervals for redundancy
    const intervals = this.options.syncSchedule!.intervals!;
    
    intervals.forEach((interval, index) => {
      const timeoutId = window.setInterval(() => {
        if (this.isOnline) {
          console.log(`Running scheduled sync #${index + 1} (${interval}ms interval)`);
          this.processQueueWithBatch();
        }
      }, interval);
      
      this.syncIntervals.push(timeoutId);
    });
    
    console.log(`Started ${intervals.length} sync intervals:`, intervals.map(i => `${i/1000}s`));
  }

  /**
   * Stop advanced sync
   */
  private stopAdvancedSync(): void {
    // Clear all sync intervals
    this.syncIntervals.forEach(intervalId => {
      clearInterval(intervalId);
    });
    this.syncIntervals = [];
    
    // Clear retry timeouts
    this.retryTimeouts.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    this.retryTimeouts.clear();
  }

  /**
   * Set up visibility change detection
   */
  private setupVisibilityChangeDetection(): void {
    if (!this.options.syncSchedule!.onVisibilityChange) return;
    
    const handleVisibilityChange = () => {
      if (!document.hidden && this.isOnline) {
        console.log('Page became visible, triggering sync...');
        this.processQueueWithBatch();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
  }

  /**
   * Process queue with batch support
   */
  private async processQueueWithBatch(): Promise<void> {
    if (!this.isOnline) {
      console.log('Cannot process queue while offline');
      return;
    }

    try {
      const requests = await this.getPendingRequests();
      
      if (requests.length === 0) {
        console.log('No pending requests to process');
        return;
      }
      
      const batchSize = this.options.syncSchedule!.batchSize!;
      const sortedRequests = this.sortRequestsByPriority(requests);
      
      console.log(`Processing ${Math.min(batchSize, requests.length)} of ${requests.length} pending requests...`);
      
      // Process in batches
      const batch = sortedRequests.slice(0, batchSize);
      
      for (const request of batch) {
        try {
          await this.processRequest(request);
        } catch (error) {
          console.error(`Failed to process request ${request.id}:`, error);
          await this.handleRequestFailure(request, error);
        }
      }
      
      this.lastSyncTime = Date.now();
      console.log(`Batch processing completed. ${batch.length} requests processed.`);
      
    } catch (error) {
      console.error('Error processing queue:', error);
    }
  }

  /**
   * Handle request failure with smart retry logic
   */
  private async handleRequestFailure(request: OfflineRequest, error: any): Promise<void> {
    const retryIntervals = this.options.syncSchedule!.retryIntervals!;
    const currentRetry = request.retryCount;
    
    if (currentRetry < (request.maxRetries || this.options.maxRetries)) {
      // Get retry interval based on retry count
      const retryInterval = retryIntervals[Math.min(currentRetry, retryIntervals.length - 1)];
      
      console.log(`Scheduling retry ${currentRetry + 1}/${request.maxRetries} for request ${request.id} in ${retryInterval}ms`);
      
      // Schedule retry
      const timeoutId = window.setTimeout(async () => {
        try {
          await this.incrementRetryCount(request.id!);
          await this.processRequest(request);
          console.log(`Retry successful for request ${request.id}`);
        } catch (retryError) {
          console.error(`Retry failed for request ${request.id}:`, retryError);
          await this.handleRequestFailure(request, retryError);
        } finally {
          this.retryTimeouts.delete(request.id!);
        }
      }, retryInterval);
      
      this.retryTimeouts.set(request.id!, timeoutId);
      await this.incrementRetryCount(request.id!);
      
    } else {
      // Mark as failed after max retries
      console.log(`Request ${request.id} failed after ${request.maxRetries} retries`);
      await this.markRequestAsFailed(request.id!);
    }
  }

  /**
   * Clean up old requests based on maxAge
   */
  private async cleanupOldRequests(): Promise<void> {
    try {
      const maxAge = this.options.syncSchedule!.maxAge!;
      const cutoffTime = Date.now() - maxAge;
      
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const requests = await new Promise<OfflineRequest[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      const oldRequests = requests.filter(r => r.timestamp < cutoffTime);
      let cleanedCount = 0;
      
      for (const request of oldRequests) {
        await this.removeRequest(request.id!);
        cleanedCount++;
      }
      
      if (cleanedCount > 0) {
        console.log(`Cleaned up ${cleanedCount} old requests older than ${maxAge/1000}s`);
      }
      
    } catch (error) {
      console.error('Failed to cleanup old requests:', error);
    }
  }

  /**
   * Queue a request for offline processing
   */
  async queueRequest(request: Request, options?: Partial<OfflineRequest>): Promise<number> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      // Check queue size limit
      const count = await this.getRequestCount();
      if (count >= this.options.maxQueueSize) {
        throw new Error('Offline queue is full');
      }
      
      const requestData: OfflineRequest = {
        url: request.url,
        method: request.method,
        headers: Object.fromEntries(request.headers.entries()),
        body: request.method !== 'GET' && request.method !== 'HEAD' ? await request.clone().text() : null,
        timestamp: Date.now(),
        retryCount: 0,
        maxRetries: options?.maxRetries || this.options.maxRetries,
        priority: options?.priority || 'normal',
        tags: options?.tags || []
      };
      
      const requestId = await new Promise<number>((resolve, reject) => {
        const addRequest = store.add(requestData);
        addRequest.onsuccess = () => resolve(addRequest.result as number);
        addRequest.onerror = () => reject(addRequest.error);
      });
      
      // Trigger immediate sync if enabled and online
      if (this.options.syncSchedule!.immediate && this.isOnline) {
        console.log(`Triggering immediate sync for request ${requestId} (${request.method} ${request.url})`);
        // Use setTimeout to avoid blocking the queue operation
        setTimeout(() => {
          this.processQueueWithBatch();
        }, 100);
      }
      
      return requestId;
      
    } catch (error) {
      console.error('Failed to queue request:', error);
      throw error;
    }
  }

  /**
   * Process the offline queue
   */
  async processQueue(): Promise<void> {
    if (!this.isOnline) {
      console.log('Cannot process queue while offline');
      return;
    }

    try {
      const requests = await this.getPendingRequests();
      
      if (requests.length === 0) {
        console.log('No pending requests to process');
        return;
      }
      
      console.log(`Processing ${requests.length} pending requests...`);
      
      // Sort requests by priority and timestamp
      const sortedRequests = this.sortRequestsByPriority(requests);
      
      for (const request of sortedRequests) {
        try {
          await this.processRequest(request);
        } catch (error) {
          console.error(`Failed to process request ${request.id}:`, error);
          
          // Increment retry count
          if (request.retryCount < (request.maxRetries || this.options.maxRetries)) {
            await this.incrementRetryCount(request.id!);
          } else {
            // Mark as failed after max retries
            await this.markRequestAsFailed(request.id!);
          }
        }
      }
      
      console.log('Queue processing completed');
      
    } catch (error) {
      console.error('Error processing queue:', error);
    }
  }

  /**
   * Process a single request
   */
  private async processRequest(request: OfflineRequest): Promise<void> {
    const fetchOptions: RequestInit = {
      method: request.method,
      headers: request.headers,
      mode: 'cors',
      credentials: 'same-origin'
    };
    
    if (request.body) {
      fetchOptions.body = request.body;
    }
    
    const response = await fetch(request.url, fetchOptions);
    
    if (response.ok) {
      console.log(`Successfully processed request: ${request.method} ${request.url}`);
      await this.removeRequest(request.id!);
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  /**
   * Get pending requests
   */
  private async getPendingRequests(): Promise<OfflineRequest[]> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get request count
   */
  private async getRequestCount(): Promise<number> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Sort requests by priority and timestamp
   */
  private sortRequestsByPriority(requests: OfflineRequest[]): OfflineRequest[] {
    const priorityOrder = { high: 3, normal: 2, low: 1 };
    
    return requests.sort((a, b) => {
      const priorityDiff = (priorityOrder[a.priority || 'normal'] || 2) - (priorityOrder[b.priority || 'normal'] || 2);
      if (priorityDiff !== 0) return -priorityDiff; // High priority first
      
      // Then by timestamp (oldest first)
      return a.timestamp - b.timestamp;
    });
  }

  /**
   * Increment retry count
   */
  private async incrementRetryCount(id: number): Promise<void> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    
    const getRequest = store.get(id);
    
    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const request = getRequest.result;
        if (request) {
          request.retryCount++;
          const updateRequest = store.put(request);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Request not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Mark request as failed
   */
  private async markRequestAsFailed(id: number): Promise<void> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    
    const getRequest = store.get(id);
    
    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const request = getRequest.result;
        if (request) {
          request.tags = [...(request.tags || []), 'failed'];
          const updateRequest = store.put(request);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Request not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Remove a request from the queue
   */
  private async removeRequest(id: number): Promise<void> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get queue statistics
   */
  async getQueueStats(): Promise<OfflineQueueStats> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      const requests = await new Promise<OfflineRequest[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      const stats: OfflineQueueStats = {
        totalRequests: requests.length,
        pendingRequests: requests.filter(r => !r.tags?.includes('failed')).length,
        failedRequests: requests.filter(r => r.tags?.includes('failed')).length,
        successfulRequests: 0, // This would need to be tracked separately
        oldestRequest: requests.length > 0 ? Math.min(...requests.map(r => r.timestamp)) : undefined,
        newestRequest: requests.length > 0 ? Math.max(...requests.map(r => r.timestamp)) : undefined
      };
      
      return stats;
      
    } catch (error) {
      console.error('Failed to get queue stats:', error);
      throw error;
    }
  }

  /**
   * Clear the entire queue
   */
  async clearQueue(): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
      
    } catch (error) {
      console.error('Failed to clear queue:', error);
      throw error;
    }
  }

  /**
   * Remove failed requests
   */
  async removeFailedRequests(): Promise<number> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const requests = await new Promise<OfflineRequest[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      const failedRequests = requests.filter(r => r.tags?.includes('failed'));
      let removedCount = 0;
      
      for (const request of failedRequests) {
        await this.removeRequest(request.id!);
        removedCount++;
      }
      
      return removedCount;
      
    } catch (error) {
      console.error('Failed to remove failed requests:', error);
      throw error;
    }
  }

  /**
   * Get requests by tag
   */
  async getRequestsByTag(tag: string): Promise<OfflineRequest[]> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      const requests = await new Promise<OfflineRequest[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      return requests.filter(r => r.tags?.includes(tag));
      
    } catch (error) {
      console.error('Failed to get requests by tag:', error);
      throw error;
    }
  }

  /**
   * Check if service is supported
   */
  isSupported(): boolean {
    return 'indexedDB' in window && 'serviceWorker' in navigator;
  }

  /**
   * Check if currently online
   */
  getOnlineStatus(): boolean {
    return this.isOnline;
  }

  /**
   * Get current options
   */
  getOptions(): Required<OfflineQueueOptions> {
    return { ...this.options };
  }

  /**
   * Update options
   */
  updateOptions(newOptions: Partial<OfflineQueueOptions>): void {
    this.options = { ...this.options, ...newOptions };
    
    // Restart advanced sync if sync schedule changed
    if (newOptions.syncSchedule && this.isOnline) {
      this.startAdvancedSync();
    }
  }

  /**
   * Configure sync timing
   */
  configureSyncTiming(config: {
    immediate?: boolean;
    intervals?: number[];
    maxAge?: number;
    batchSize?: number;
    retryIntervals?: number[];
  }): void {
    this.updateOptions({
      syncSchedule: {
        ...this.options.syncSchedule,
        ...config
      }
    });
    
    console.log('Sync timing configured:', config);
  }

  /**
   * Get sync status
   */
  getSyncStatus(): {
    isOnline: boolean;
    lastSyncTime: number;
    nextSyncEstimate: number;
    activeIntervals: number;
    pendingRetries: number;
  } {
    const intervals = this.options.syncSchedule!.intervals!;
    const shortestInterval = Math.min(...intervals);
    const timeSinceLastSync = Date.now() - this.lastSyncTime;
    const nextSyncEstimate = Math.max(0, shortestInterval - timeSinceLastSync);
    
    return {
      isOnline: this.isOnline,
      lastSyncTime: this.lastSyncTime,
      nextSyncEstimate,
      activeIntervals: this.syncIntervals.length,
      pendingRetries: this.retryTimeouts.size
    };
  }

  /**
   * Force immediate sync
   */
  async forceSyncNow(): Promise<void> {
    if (!this.isOnline) {
      throw new Error('Cannot sync while offline');
    }
    
    console.log('Forcing immediate sync...');
    await this.processQueueWithBatch();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopAdvancedSync();
    window.removeEventListener('online', this.setupOnlineDetection.bind(this));
    window.removeEventListener('offline', this.setupOnlineDetection.bind(this));
    document.removeEventListener('visibilitychange', this.setupVisibilityChangeDetection.bind(this));
    window.removeEventListener('focus', this.setupVisibilityChangeDetection.bind(this));
  }
}

// Create singleton instance
export const offlineQueueService = new OfflineQueueService();
